import type { Config } from "@packages/config";
import { CONFIG } from "@packages/config";
import { window } from "@packages/global";
import { Inject, Injectable } from "@webmotors/ioc";
import { Logger } from "../logger.contract";
import { LOGGER } from "../logger.types";

@Injectable(LOGGER)
export class ConsoleService implements Logger {
  private level: "error" | "warn" | "info" | "log" | "debug";

  constructor(
    @Inject(CONFIG)
    private readonly config: Config,
    private context: string = "GLOBAL",
  ) {
    const envLevel = (process.env.LOG_LEVEL || "").trim().toLowerCase();
    this.level = ["error", "warn", "info", "log", "debug"].includes(envLevel)
      ? (envLevel as "error" | "warn" | "info" | "log" | "debug")
      : "warn";
  }

  private shouldLog(level: string): boolean {
    if (window?.__ENABLE_LOGGING__ === true) {
      return true;
    }

    const levels = ["error", "warn", "info", "log", "debug"];
    return levels.indexOf(level) <= levels.indexOf(this.level);
  }

  /**
   * Create a new logger with context
   *
   * @param context
   */
  create(context: string): Logger {
    return new ConsoleService(this.config, context);
  }

  /**
   * Writes a log message
   *
   * @param messages
   * @returns
   */
  log(...messages: any[]): void | Promise<void> {
    if (!this.config.logging || !this.shouldLog("log")) {
      return;
    }

    console.log(`[${this.context}]`, ...messages);
  }

  /**
   * Writes a info message
   *
   * @param messages
   * @returns
   */
  info(...messages: any[]): void | Promise<void> {
    if (!this.config.logging || !this.shouldLog("info")) {
      return;
    }

    console.info(`[${this.context}]`, ...messages);
  }

  /**
   * Writes a warn message
   *
   * @param messages
   * @returns
   */
  warn(...messages: any[]): void | Promise<void> {
    if (!this.config.logging || !this.shouldLog("warn")) {
      return;
    }

    console.warn(`[${this.context}]`, ...messages);
  }

  /**
   * Writes a debug message
   *
   * @param messages
   * @returns
   */
  debug(...messages: any[]): void | Promise<void> {
    if (!this.config.logging || !this.shouldLog("debug")) {
      return;
    }

    console.debug(`[${this.context}]`, ...messages);
  }

  /**
   * Writes a error message
   *
   * @param messages
   * @returns
   */
  error(...messages: any[]): void | Promise<void> {
    if (!this.config.logging || !this.shouldLog("error")) {
      return;
    }

    console.error(`[${this.context}]`, ...messages);
  }
}
